/**
 * US states — MIRROR of apps/contractor-app/src/utils/usStates.js and
 * apps/customer-portal/src/utils/usStates.js. All THREE are copies on purpose; if you edit
 * one, edit the other two.
 *
 * Why a third copy rather than an import: the two CRA apps are built and deployed
 * separately and each resolves `../../utils/usStates` inside its own src tree, so neither
 * app-level copy is reachable from this package. Consolidating the three into this one is a
 * real option, but it repoints ~15 call sites across both apps and cannot be validated
 * locally (contractor-app node_modules is not installed here; only a green Vercel build
 * proves a CRA change) — so it is a deliberate separate job, not a drive-by.
 *
 * Why normalising exists at all: rows written before the dropdowns existed hold full names
 * or worse. Verified live 2026-08-24, `customers.billing_state` holds "ORegon" (typo),
 * "85018AZ" (zip+state run together), "PhoenixAZ" (city+state), "33101" (a zip in the state
 * column), plus the legitimately non-US "Greater London" and "NSW".
 */

export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

/**
 * Map any state value to its canonical 2-letter USPS code.
 * - full name, case-insensitive ("Oregon" / "oregon") -> "OR"
 * - existing 2-letter code, any case ("az") -> "AZ"
 * - anything else (international / unrecognized, e.g. "NSW", "Greater London") -> trimmed passthrough, unchanged
 * Safe to render as a <select value>: legacy full-name data selects the right option;
 * unrecognized values simply fall through to the empty placeholder.
 */
export function normalizeStateToCode(value) {
  if (!value) return '';
  const v = String(value).trim();
  if (!v) return '';
  const lower = v.toLowerCase();
  const byCode = US_STATES.find((s) => s.code.toLowerCase() === lower);
  if (byCode) return byCode.code;
  const byName = US_STATES.find((s) => s.name.toLowerCase() === lower);
  if (byName) return byName.code;
  return v;
}
